import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, CreditCard, CheckCircle, XCircle, Clock, AlertTriangle, Upload, Camera, Loader2, Eye, EyeOff, HelpCircle, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { panAPI, aadhaarAPI, userAPI } from '../services/api';
import { AadhaarQrUpload } from '../components/kyc/AadhaarQrUpload';

const KYCPageNew = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);

  // Aadhaar State
  const [aadhaarStatus, setAadhaarStatus] = useState(null);
  const [aadhaarLoading, setAadhaarLoading] = useState(true);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [aadhaarName, setAadhaarName] = useState('');
  const [aadhaarSubmitting, setAadhaarSubmitting] = useState(false);
  const [aadhaarAddress, setAadhaarAddress] = useState(user?.address || '');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // PAN State
  const [panStatus, setPanStatus] = useState(null);
  const [panLoading, setPanLoading] = useState(true);
  const [panNumber, setPanNumber] = useState('');
  const [panPhoto, setPanPhoto] = useState(null);
  const [panPhotoPreview, setPanPhotoPreview] = useState(null);
  const [panSubmitting, setPanSubmitting] = useState(false);
  const [showPanNumber, setShowPanNumber] = useState(false);

  // Fetch statuses on mount
  useEffect(() => {
    fetchAadhaarStatus();
    fetchPanStatus();
  }, []);

  const fetchAadhaarStatus = async () => {
    setAadhaarLoading(true);
    try {
      const response = await aadhaarAPI.getStatus();
      setAadhaarStatus(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch Aadhaar status:', error);
      }
    } finally {
      setAadhaarLoading(false);
    }
  };

  const fetchPanStatus = async () => {
    setPanLoading(true);
    try {
      const response = await panAPI.getStatus();
      setPanStatus(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch PAN status:', error);
      }
    } finally {
      setPanLoading(false);
    }
  };

  // Format Aadhaar number with spaces
  const formatAadhaar = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 12);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const handleAadhaarChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
    setAadhaarNumber(digits);
  };

  // Handle Aadhaar submission (manual input)
  const handleAadhaarSubmit = async () => {
    if (aadhaarNumber.length !== 12) {
      toast.error('Please enter a valid 12-digit Aadhaar number');
      return;
    }
    if (!aadhaarName.trim() || aadhaarName.trim().length < 3) {
      toast.error('Please enter your name as per Aadhaar');
      return;
    }
    if (aadhaarAddress.trim().length < 10) {
      toast.error('Please enter your complete address as per Aadhaar');
      return;
    }
    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    setAadhaarSubmitting(true);
    try {
      // Update KYC details
      await userAPI.updateKYC({
        name: aadhaarName.trim(),
        aadhaar_number: aadhaarNumber,
        address: aadhaarAddress.trim(),
        terms_accepted: termsAccepted,
      });
      toast.success('Aadhaar details submitted successfully');
      await refreshUser();
      await fetchAadhaarStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit Aadhaar details');
    } finally {
      setAadhaarSubmitting(false);
    }
  };

  // Format PAN number input
  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    setPanNumber(value);
  };

  // Handle PAN photo upload
  const handlePanPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPanPhoto(reader.result);
      setPanPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle PAN submission
  const handlePanSubmit = async () => {
    if (!panNumber || panNumber.length !== 10) {
      toast.error('Please enter a valid 10-character PAN number');
      return;
    }

    // Validate PAN format: ABCDE1234F
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(panNumber)) {
      toast.error('Invalid PAN format. Expected: ABCDE1234F');
      return;
    }

    if (!panPhoto) {
      toast.error('Please upload a photo of your PAN card');
      return;
    }

    setPanSubmitting(true);
    try {
      await panAPI.submit(panNumber, panPhoto);
      toast.success('PAN submitted successfully! Awaiting verification.');
      await fetchPanStatus();
      setPanNumber('');
      setPanPhoto(null);
      setPanPhotoPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit PAN');
    } finally {
      setPanSubmitting(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
            <CheckCircle className="w-3.5 h-3.5" />
            Verified
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
            <Clock className="w-3.5 h-3.5" />
            Pending Verification
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
            <XCircle className="w-3.5 h-3.5" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
            <AlertTriangle className="w-3.5 h-3.5" />
            Not Submitted
          </span>
        );
    }
  };

  // Check if Aadhaar is verified (from user object or status)
  const isAadhaarVerified = user?.kyc_verified || aadhaarStatus?.status === 'verified';
  const isPanVerified = panStatus?.pan_status === 'verified';
  const isPanPending = panStatus?.pan_status === 'pending';

  return (
    <div className="min-h-screen pb-8" data-testid="kyc-page-new">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#004D40] to-[#00302B] px-4 py-4 sticky top-16 z-10 mx-2 rounded-lg shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-xl text-white font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
              KYC Verification
            </h1>
            <p className="text-xs text-white/70">Complete your identity verification</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-[#F5E6C8]/50 rounded-xl p-4 border border-[#B8860B]/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#B8860B]/20 flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-[#B8860B]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#004D40] mb-1">Why KYC is Required?</h3>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• <strong>Aadhaar verification</strong> is required for booking and refunding gold</li>
                <li>• <strong>PAN verification</strong> is mandatory for transactions above ₹2,00,000</li>
                <li>• Your details are encrypted and stored securely</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Aadhaar Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/30 overflow-hidden shadow-lg">
          <div className="flex items-center justify-between p-4 border-b border-[#B8860B]/10 bg-gradient-to-r from-[#004D40] to-[#00302B]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Aadhaar Verification</h3>
                <p className="text-xs text-white/70">Required for basic transactions</p>
              </div>
            </div>
            {!aadhaarLoading && getStatusBadge(isAadhaarVerified ? 'verified' : 'not_submitted')}
          </div>

          <div className="p-4">
            {aadhaarLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#004D40]" />
              </div>
            ) : isAadhaarVerified ? (
              /* Verified State */
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Aadhaar Verified</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs">Name</p>
                      <p className="font-medium text-gray-800">{user?.name || aadhaarStatus?.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Aadhaar</p>
                      <p className="font-medium text-gray-800">XXXX XXXX {aadhaarStatus?.aadhaar_last_4 || user?.aadhaar_last_4 || 'XXXX'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <HelpCircle className="w-4 h-4" />
                  <span>To update Aadhaar details, please contact support</span>
                </div>
              </div>
            ) : (
              /* Input Form */
              <div className="space-y-4">
                <AadhaarQrUpload onVerified={async () => { await refreshUser(); await fetchAadhaarStatus(); }} />
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <div className="flex-1 h-px bg-gray-200" />or enter manually<div className="flex-1 h-px bg-gray-200" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#004D40] mb-1.5">
                    Name (as per Aadhaar) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={aadhaarName}
                    onChange={(e) => setAadhaarName(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-[#B8860B]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40]/50 text-[#004D40]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#004D40] mb-1.5">
                    Aadhaar Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formatAadhaar(aadhaarNumber)}
                    onChange={handleAadhaarChange}
                    placeholder="XXXX XXXX XXXX"
                    className="w-full px-4 py-3 border border-[#B8860B]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40]/50 text-[#004D40] tracking-wider font-mono"
                    maxLength={14}
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter your 12-digit Aadhaar number</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#004D40] mb-1.5">
                    Address (as per Aadhaar) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    data-testid="kyc-manual-address-input"
                    rows={3}
                    maxLength={500}
                    value={aadhaarAddress}
                    onChange={(e) => setAadhaarAddress(e.target.value)}
                    placeholder="House / flat, street, area, city, state, PIN code"
                    className="w-full px-4 py-3 border border-[#B8860B]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40]/50 text-[#004D40] resize-none"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer" data-testid="kyc-manual-terms-label">
                  <input
                    data-testid="kyc-manual-terms-checkbox"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 accent-[#004D40]"
                  />
                  <span>
                    I agree to the{' '}
                    <a href="/terms-new" target="_blank" rel="noreferrer" className="text-[#B8860B] underline">Terms &amp; Conditions</a>
                    {' '}and{' '}
                    <a href="/privacy-new" target="_blank" rel="noreferrer" className="text-[#B8860B] underline">Privacy Policy</a>
                  </span>
                </label>
                <button
                  onClick={handleAadhaarSubmit}
                  disabled={aadhaarSubmitting || aadhaarNumber.length !== 12 || !aadhaarName.trim() || aadhaarAddress.trim().length < 10 || !termsAccepted}
                  className="w-full py-3 bg-gradient-to-r from-[#004D40] to-[#00302B] text-white font-medium rounded-lg hover:from-[#00302B] hover:to-[#001A15] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {aadhaarSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Submit Aadhaar Details
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* PAN Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/30 overflow-hidden shadow-lg">
          <div className="flex items-center justify-between p-4 border-b border-[#B8860B]/10 bg-gradient-to-r from-[#B8860B] to-[#8B6914]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">PAN Verification</h3>
                <p className="text-xs text-white/70">Required for transactions above ₹2 lakhs</p>
              </div>
            </div>
            {!panLoading && getStatusBadge(panStatus?.pan_status || 'not_submitted')}
          </div>

          <div className="p-4">
            {panLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#B8860B]" />
              </div>
            ) : isPanVerified ? (
              /* Verified State */
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">PAN Verified</span>
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-500 text-xs">PAN Number</p>
                    <p className="font-medium text-gray-800 font-mono">XXXXXX{panStatus?.pan_last_4 || 'XXXX'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <HelpCircle className="w-4 h-4" />
                  <span>To update PAN details, please contact support</span>
                </div>
              </div>
            ) : isPanPending ? (
              /* Pending State */
              <div className="space-y-4">
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">Verification in Progress</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Your PAN (XXXXXX{panStatus?.pan_last_4}) is being verified. This usually takes 1-2 business days.
                  </p>
                </div>
              </div>
            ) : panStatus?.pan_status === 'rejected' ? (
              /* Rejected State */
              <div className="space-y-4">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200 mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="text-sm font-medium text-red-800">PAN Rejected</span>
                  </div>
                  <p className="text-sm text-red-700">
                    {panStatus?.pan_rejection_reason || 'Your PAN submission was rejected. Please resubmit with correct details.'}
                  </p>
                </div>
                {/* Show form for resubmission */}
                {renderPanForm()}
              </div>
            ) : (
              /* Input Form */
              renderPanForm()
            )}
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-[#004D40] mb-3">Need Help?</h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="mailto:support@mothersgold.in" className="flex items-center gap-2 text-[#B8860B] hover:text-[#8B6914]">
              <Mail className="w-4 h-4" />
              support@mothersgold.in
            </a>
            <a href="tel:+918137830916" className="flex items-center gap-2 text-[#B8860B] hover:text-[#8B6914]">
              <Phone className="w-4 h-4" />
              +91 8137 830 916
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  // PAN Form Component
  function renderPanForm() {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#004D40] mb-1.5">
            PAN Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPanNumber ? 'text' : 'password'}
              value={panNumber}
              onChange={handlePanChange}
              placeholder="ABCDE1234F"
              className="w-full px-4 py-3 border border-[#B8860B]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B8860B]/50 text-[#004D40] tracking-wider font-mono uppercase pr-12"
              maxLength={10}
            />
            <button
              type="button"
              onClick={() => setShowPanNumber(!showPanNumber)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPanNumber ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#004D40] mb-1.5">
            PAN Card Photo <span className="text-red-500">*</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePanPhotoSelect}
            className="hidden"
          />
          
          {panPhotoPreview ? (
            <div className="relative">
              <img
                src={panPhotoPreview}
                alt="PAN Card"
                className="w-full h-48 object-contain bg-gray-100 rounded-lg border border-[#B8860B]/30"
              />
              <button
                onClick={() => {
                  setPanPhoto(null);
                  setPanPhotoPreview(null);
                }}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 border-2 border-dashed border-[#B8860B]/40 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#B8860B] hover:bg-[#F5E6C8]/20 transition-colors"
            >
              <Upload className="w-8 h-8 text-[#B8860B]" />
              <span className="text-sm text-[#004D40]">Click to upload PAN card photo</span>
              <span className="text-xs text-gray-500">JPG, PNG up to 5MB</span>
            </button>
          )}
        </div>

        <button
          onClick={handlePanSubmit}
          disabled={panSubmitting || panNumber.length !== 10 || !panPhoto}
          className="w-full py-3 bg-gradient-to-r from-[#B8860B] to-[#8B6914] text-white font-medium rounded-lg hover:from-[#8B6914] hover:to-[#6B5000] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {panSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" />
              Submit PAN for Verification
            </>
          )}
        </button>
      </div>
    );
  }
};

export default KYCPageNew;
